import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Immutable from 'immutable';
import { Route, Switch } from 'react-router';
import { useSelector } from 'react-redux';

import {
  I18nProvider,
  EntityMapperContext,
  ENTITY_LOADING_STATUS_START,
  ENTITY_LOADING_STATUS_LOADING,
  View,
} from '@uncinc/react-drupal-core';

import blocksMapper from '../../mappers/blocks';
import nodeMapper from '../../mappers/node';
import paragraphMapper from '../../mappers/paragraphs';
import taxonomyMapper from '../../mappers/taxonomy';
import viewsMapper from '../../mappers/views';

import {
  Header,
  Footer,
} from '../../implementations';

import i18next from '../../config/i18next';
import Entity from '../Entity';
import SearchPage from '../SearchPage';
import Cookie from '../../components/Cookie';
import CustomRoute from '../CustomRoute';

import './style.scss';
import '../../styles/main.scss';


const App = () => {
  const entityId = useSelector((state) => state.getIn(['entity', 'entityId'], null));
  const entityType = useSelector((state) => state.getIn(['entity', 'entityType'], null));
  // `|| Immutable.Map()` too: a stored `fromJS(undefined)` leaves the key present,
  // and Immutable only substitutes notSetValue for a *missing* key.
  const childEntity = useSelector((state) => state.getIn([entityType, entityId], Immutable.Map()) || Immutable.Map());
  const rootClasses = useSelector((state) => state.getIn(['ui', 'rootClasses']));
  const language = useSelector((state) => state.getIn(['ui', 'language']));
  const entityExists = useSelector((state) => state.getIn(['entity', 'exists']));
  const entityLoadingStatus = useSelector((state) => state.getIn(['entity', 'loadingStatus']));
  const crisisBannerTimeStamp = useSelector((state) => state.getIn(['view', 'crisis_banner', 'block_1', 'entities', 0, 'changed', 0, 'value'], null));
  const [crisisBannerHeight, setCrisisBannerHeight] = useState(0);

  const animationFrameRef = useRef();
  const crisisBannerRef = useRef();
  const headerRef = useRef();
  const mainRef = useRef();

  const classes = classNames('App', rootClasses.join(' '), {
    'is-front-page': childEntity.get('is_front'),
    'not-found': !entityExists,
    'entity--loading': (entityLoadingStatus === ENTITY_LOADING_STATUS_START || entityLoadingStatus === ENTITY_LOADING_STATUS_LOADING),
    'client-side': !global.ssr,
  });

  const entityTypeMapper = {
    block: blocksMapper,
    node: nodeMapper,
    paragraph: paragraphMapper,
    taxonomy: taxonomyMapper,
    view: viewsMapper,
  };

  const alignHeaderToCrisisBanner = () => {
    if (crisisBannerRef.current && headerRef.current && mainRef.current) {
      headerRef.current.style.marginTop = crisisBannerRef.current.getBoundingClientRect().height + 'px';
      mainRef.current.style.setProperty('--crisisbanner-height', `${crisisBannerRef.current.getBoundingClientRect().height}px`);
      setCrisisBannerHeight(crisisBannerRef.current.getBoundingClientRect().height);
    }

    animationFrameRef.current = requestAnimationFrame(alignHeaderToCrisisBanner);
  };

  useEffect(() => {
    // Call once to align on initial load.
    alignHeaderToCrisisBanner();

    const handleResize = () => {
      alignHeaderToCrisisBanner();
    };

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Remove event listener on cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [crisisBannerRef, headerRef, mainRef]); // Dependency array ensures useEffect is called when refs change


  useEffect(() => {
    animationFrameRef.current = requestAnimationFrame(alignHeaderToCrisisBanner);

    return () => cancelAnimationFrame(animationFrameRef.current);
  }, [crisisBannerRef, headerRef, mainRef]);

  return (
    <EntityMapperContext.Provider value={entityTypeMapper}>
      <I18nProvider locale={language} i18next={i18next}>
        <div className={classes} language={language}>
          <View
            id="crisis_banner"
            display="block_1"
            forwardRef={crisisBannerRef}
          />
          <Header
            crisisBannerTimeStamp={crisisBannerTimeStamp}
            forwardRef={headerRef}
            crisisBannerHeight={crisisBannerHeight}
          />
          <main className="main" ref={mainRef}>
            <Switch>
              {/* Add routes here */}
              <CustomRoute path="/:path(zoeken|search)" render={() => (<SearchPage />)} />
              <Route render={() => (<Entity />)} />
            </Switch>
          </main>
          <Footer />
          <Cookie />
          <div id="modal-root" />
        </div>
      </I18nProvider>
    </EntityMapperContext.Provider>
  );
};

export default App;
